import {
  DOMAINS,
  type AttemptResult,
  type DailyPlan,
  type Difficulty,
  type Domain,
  type DomainMastery,
  type LevelDefinition
} from "@/domain";

import { clampDifficulty, clampScore } from "./scoring";

const DEFAULT_ACTIVITY_COUNT = 4;
const MIN_ACTIVITY_COUNT = 3;
const MAX_ACTIVITY_COUNT = 4;
const DEFAULT_TARGET_MINUTES = 15;
const NEW_DOMAIN_SCORE = 50;

export interface RecommendationContext<TPayload = unknown> {
  profileId: string;
  levels: readonly LevelDefinition<TPayload>[];
  attempts: readonly AttemptResult[];
  mastery: readonly DomainMastery[];
}

export interface RankedLevel<TPayload = unknown> {
  level: LevelDefinition<TPayload>;
  recommendedDifficulty: Difficulty;
  difficultyDistance: number;
  isReplay: boolean;
  lastCompletedAt?: string;
}

export interface DailyPlanInput<TPayload = unknown>
  extends RecommendationContext<TPayload> {
  date: string;
  activityCount?: number;
  targetMinutes?: number;
}

function compareText(left: string, right: string): number {
  if (left < right) {
    return -1;
  }

  return left > right ? 1 : 0;
}

function compareRankedLevels<TPayload>(
  left: RankedLevel<TPayload>,
  right: RankedLevel<TPayload>
): number {
  const difficultyDifference =
    left.difficultyDistance - right.difficultyDistance;

  if (difficultyDifference !== 0) {
    return difficultyDifference;
  }

  if (left.isReplay !== right.isReplay) {
    return left.isReplay ? 1 : -1;
  }

  const recencyDifference = compareText(
    left.lastCompletedAt ?? "",
    right.lastCompletedAt ?? ""
  );

  return recencyDifference || compareText(left.level.id, right.level.id);
}

function latestAttemptByLevel(
  profileId: string,
  attempts: readonly AttemptResult[]
): Map<string, AttemptResult> {
  const latestByLevel = new Map<string, AttemptResult>();

  for (const attempt of attempts) {
    if (attempt.profileId !== profileId) {
      continue;
    }

    const previous = latestByLevel.get(attempt.levelId);
    const isLater =
      previous === undefined ||
      compareText(previous.completedAt, attempt.completedAt) < 0 ||
      (previous.completedAt === attempt.completedAt &&
        compareText(previous.id, attempt.id) < 0);

    if (isLater) {
      latestByLevel.set(attempt.levelId, attempt);
    }
  }

  return latestByLevel;
}

// This ranking is advisory: every supplied level remains present and replayable.
export function rankLevelsForExploration<TPayload>(
  context: RecommendationContext<TPayload>
): RankedLevel<TPayload>[] {
  const masteryByDomain = new Map(
    context.mastery.map((mastery) => [mastery.domain, mastery] as const)
  );
  const latestByLevel = latestAttemptByLevel(
    context.profileId,
    context.attempts
  );

  return context.levels
    .map((level): RankedLevel<TPayload> => {
      const recommendedDifficulty = clampDifficulty(
        masteryByDomain.get(level.domain)?.recommendedDifficulty ?? 1
      );
      const latestAttempt = latestByLevel.get(level.id);
      const rankedLevel: RankedLevel<TPayload> = {
        level,
        recommendedDifficulty,
        difficultyDistance: Math.abs(
          level.difficulty - recommendedDifficulty
        ),
        isReplay: latestAttempt !== undefined
      };

      if (latestAttempt !== undefined) {
        rankedLevel.lastCompletedAt = latestAttempt.completedAt;
      }

      return rankedLevel;
    })
    .sort(compareRankedLevels);
}

function normalizeActivityCount(value: number | undefined): number {
  const requested =
    value !== undefined && Number.isFinite(value)
      ? Math.round(value)
      : DEFAULT_ACTIVITY_COUNT;

  return Math.min(
    MAX_ACTIVITY_COUNT,
    Math.max(MIN_ACTIVITY_COUNT, requested)
  );
}

function normalizeTargetMinutes(value: number | undefined): number {
  return value !== undefined && Number.isFinite(value) && value > 0
    ? Math.max(1, Math.round(value))
    : DEFAULT_TARGET_MINUTES;
}

function domainIndex(domain: Domain): number {
  return DOMAINS.indexOf(domain);
}

export function buildDailyPlan<TPayload>(
  input: DailyPlanInput<TPayload>
): DailyPlan {
  const activityCount = normalizeActivityCount(input.activityCount);
  const rankedLevels = rankLevelsForExploration(input);
  const masteryByDomain = new Map(
    input.mastery.map((mastery) => [mastery.domain, mastery] as const)
  );
  const levelsByDomain = new Map<Domain, RankedLevel<TPayload>[]>();

  for (const rankedLevel of rankedLevels) {
    const domainLevels = levelsByDomain.get(rankedLevel.level.domain) ?? [];
    domainLevels.push(rankedLevel);
    levelsByDomain.set(rankedLevel.level.domain, domainLevels);
  }

  const availableDomains = DOMAINS.filter((domain) =>
    levelsByDomain.has(domain)
  ).sort((left, right) => {
    const leftScore = clampScore(
      masteryByDomain.get(left)?.score ?? NEW_DOMAIN_SCORE
    );
    const rightScore = clampScore(
      masteryByDomain.get(right)?.score ?? NEW_DOMAIN_SCORE
    );

    return leftScore - rightScore || domainIndex(left) - domainIndex(right);
  });
  const selectedLevelIds: string[] = [];
  const selected = new Set<string>();

  for (const domain of availableDomains) {
    if (selectedLevelIds.length >= activityCount) {
      break;
    }

    const recommendation = levelsByDomain.get(domain)?.[0];

    if (recommendation !== undefined && !selected.has(recommendation.level.id)) {
      selectedLevelIds.push(recommendation.level.id);
      selected.add(recommendation.level.id);
    }
  }

  for (const recommendation of rankedLevels) {
    if (selectedLevelIds.length >= activityCount) {
      break;
    }

    if (!selected.has(recommendation.level.id)) {
      selectedLevelIds.push(recommendation.level.id);
      selected.add(recommendation.level.id);
    }
  }

  return {
    profileId: input.profileId,
    date: input.date,
    levelIds: selectedLevelIds,
    targetMinutes: normalizeTargetMinutes(input.targetMinutes)
  };
}
